import { Link } from 'react-router-dom';
import { Select } from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  FacebookFilled,
  InstagramOutlined,
  YoutubeFilled,
  WhatsAppOutlined,
  MailOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import {
  parseStringList,
  formatAddress,
  formatPhoneList,
  formatList,
} from '@core/utils/runtimeBrand';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import PoweredBy from '@components/base/PoweredBy';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useCopy } from '@/app/providers/useCopy';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const DEFAULT_USEFUL_LINKS = [
  { label: 'About Us', to: '/about' },
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms & Conditions', to: '/terms' },
  { label: 'FAQs', to: '/faqs' },
];

const SOCIAL_ICONS = {
  facebook: FacebookFilled,
  instagram: InstagramOutlined,
  twitter: XIcon,
  youtube: YoutubeFilled,
  whatsapp: WhatsAppOutlined,
  email: MailOutlined,
  mail: MailOutlined,
};

export default function FooterCommon() {
  const t = useCopy();
  const { language, setLanguage, options: languageOptions } = useAppLanguage();
  const { config } = useIndustry();
  const {
    brandName: runtimeBrandName,
    businessAddress: runtimeAddress,
    businessEmail: runtimeEmail,
    supportPhone: runtimeSupportPhone,
    whatsappNumber: runtimeWhatsappNumber,
    socialLinks: runtimeSocialLinks,
    faq: runtimeFaq,
  } = useRuntimeBrand();
  const { locationId } = useStoreContext();
  const { data: locations } = useLocations();
  const activeLocation = locations?.find(
    (loc) =>
      String(loc.id) === String(locationId) || String(loc.raw?.location_id) === String(locationId),
  );
  const supportContact = activeLocation?.supportContact;
  const outletAddress = formatAddress(activeLocation?.address || activeLocation?.raw?.address);
  const outletSupportPhone =
    formatPhoneList(
      supportContact?.support_phone || activeLocation?.raw?.support_contact?.support_phone,
    ) || formatPhoneList(activeLocation?.raw?.phone_number);
  const outletWhatsappPhone = formatPhoneList(
    supportContact?.whatsapp_phone || activeLocation?.raw?.support_contact?.whatsapp_phone,
  );
  const outletEmail = formatList(
    supportContact?.email ||
      activeLocation?.raw?.support_contact?.email ||
      activeLocation?.raw?.email,
  );

  const activeAccount = useAccountSelectionStore((s) =>
    s.availableAccounts.find((account) => account.id === s.activeAccountId),
  );
  const raw = activeAccount?.accountDetails
    ? { account_details: activeAccount.accountDetails }
    : null;
  const footer = config?.footer ?? {};
  const year = new Date().getFullYear();
  const brandName = runtimeBrandName || config?.copy?.appName || config?.name || 'Store';

  const usefulLinks = (footer.usefulLinks ?? DEFAULT_USEFUL_LINKS).filter(
    (link) => link.to !== '/faqs' || runtimeFaq,
  );
  const address = outletAddress || runtimeAddress || footer.address || '';
  const social = runtimeSocialLinks || footer.socialLinks || {};
  const phoneNumbersList = parseStringList(
    outletSupportPhone ||
      runtimeSupportPhone ||
      supportContact?.support_phone ||
      raw?.account_details?.mobile_number?.full_number ||
      '',
  );
  const whatsappNumbersList = parseStringList(
    outletWhatsappPhone || runtimeWhatsappNumber || supportContact?.whatsapp_phone || '',
  );
  const emailList = parseStringList(
    outletEmail || runtimeEmail || supportContact?.email || raw?.account_details?.email || '',
  );
  return (
    <footer className="common-footer">
      <div className="common-footer-inner">
        <div className="common-footer-col">
          <h4 className="common-footer-col-title">{t('legal', 'Legal')}</h4>
          <ul className="common-footer-links">
            {usefulLinks.map((link) => (
              <li key={link.label}>
                <Link to={link.to}>
                  {link.label === 'Privacy Policy'
                    ? t('privacyPolicy', 'Privacy Policy')
                    : link.label === 'Terms & Conditions'
                      ? t('termsAndConditions', 'Terms & Conditions')
                      : link.label === 'About Us'
                        ? t('aboutUs', 'About Us')
                        : link.label === 'FAQ' || link.label === 'FAQs'
                          ? t('faqs', t('faq', 'FAQs'))
                          : t(link.label, link.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="common-footer-col">
          <h4 className="common-footer-col-title">{t('address', 'Address')}</h4>
          {address && (
            <p className="common-footer-address">
              <EnvironmentOutlined />
              <span>{address}</span>
            </p>
          )}
        </div>
        <div className="common-footer-col">
          <h4 className="common-footer-col-title">{t('contactAndConnect', 'Contact & Connect')}</h4>
          {phoneNumbersList.length > 0 && (
            <p className="common-footer-phone">
              <PhoneOutlined />
              <span>{phoneNumbersList.join(', ')}</span>
            </p>
          )}
          {whatsappNumbersList.length > 0 && (
            <p className="common-footer-whatsapp">
              <WhatsAppOutlined />
              <span>{whatsappNumbersList.join(', ')}</span>
            </p>
          )}
          {emailList.length > 0 && (
            <p className="common-footer-email">
              <MailOutlined />
              <span>{emailList.join(', ')}</span>
            </p>
          )}
          <div className="common-footer-social">
            {Object.entries(social).map(([platform, href]) => {
              const Icon = SOCIAL_ICONS[platform];
              if (!Icon || !href) return null;
              return (
                <a
                  key={platform}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={platform}
                >
                  <Icon />
                </a>
              );
            })}
          </div>
          <div className="footer-region region">
            <GlobalOutlined />
            <Select
              value={language}
              onChange={setLanguage}
              options={languageOptions}
              optionLabelProp="shortLabel"
              variant="borderless"
              popupMatchSelectWidth={130}
              classNames={{ popup: { root: 'footer-language-select-dropdown' } }}
              size="small"
              color="transparent"
              suffixIcon={null}
              aria-label={t('selectLanguage', 'Select language')}
            />
          </div>
        </div>
      </div>

      <div className="common-footer-bottom">
        <div>
          © {year} {brandName}. {t('allRightsReserved', 'All rights reserved').replace(/\.$/, '')}.{' '}
          <PoweredBy />
        </div>
      </div>
    </footer>
  );
}
